import { isEmpty } from "lodash/fp";

import { get, httpDelete, patch } from "@readai/common/api";
import type {
  ApiUserEventGroup,
  ApiUserEventGroupPatch,
  EventGroupACLDeleteRequest,
  EventGroupACLPatchRequest,
  EventGroupACLResponse,
  ListUserEventGroupRequest,
  UserEventGroup,
  UserEventGroupPatchRequest,
} from "@readai/common/types";
import { omit, withQueryParamsUrl } from "@readai/common/utils";
import {
  optionalParseUTCDateTimeFromString,
  parseUTCDateTime,
} from "@readai/common/utils/datetimeUtils";

export const listEventGroups = async (
  params?: ListUserEventGroupRequest,
): Promise<UserEventGroup[]> => {
  const url = withQueryParamsUrl("/user-event-groups", {
    start_date: params?.startTime?.toISO(),
    end_date: params?.endTime?.toISO(),
    calendar_provider: params?.provider,
  });

  const events: ApiUserEventGroup[] = await get(url, {
    withCreds: true,
  });

  if (isEmpty(events)) return [];

  return events.map((e) => ({
    ...e,
    endTime: parseUTCDateTime(e.endTime),
    isFlexible: e.isFlexible ?? false,
    isPrivate: e.isPrivate ?? false,
    lastFlexibleMove: optionalParseUTCDateTimeFromString(e.lastFlexibleMove),
    numAttendees: e.attendeeCount,
    recurringEventId: e.userRecurringEventId,
    sendMeetingRecapToMe: e.sendMeetingRecapToMe,
    sendMeetingRecapToParticipantsWithAccess:
      e.sendMeetingRecapToParticipantsWithAccess,
    startTime: parseUTCDateTime(e.startTime),
    title: e.title,
  }));
};

export const patchEventGroup = async ({
  id,
  body,
}: UserEventGroupPatchRequest) => {
  const apiBody: ApiUserEventGroupPatch = omit("liveNotesEnabled", body);

  if (body.liveNotesEnabled != null) {
    // this field is different both in name and type on the patch endpoint
    // for some reason
    apiBody.liveNotesOverride = body.liveNotesEnabled
      ? "user_opt_in"
      : "user_opt_out";
  }

  return patch(`/user-event-groups/${id}`, apiBody, { withCreds: true });
};

export const getEventGroupACL = async (
  id: string,
): Promise<EventGroupACLResponse> => {
  return get(`/user-event-groups/${id}/acl`, { withCreds: true });
};

export const patchEventGroupACL = async (
  id: string,
  body: EventGroupACLPatchRequest,
): Promise<void> => {
  return patch(`/user-event-groups/${id}/acl`, body, { withCreds: true });
};

export const deleteEventGroupACL = async (
  id: string,
  body: EventGroupACLDeleteRequest,
): Promise<void> => {
  const params = {
    event_group_id: id,
    entity_id: body.entityId,
    entity_kind: body.kind,
    ...(body.applyToRecurringEvent && {
      apply_to_recurring_event: body.applyToRecurringEvent,
    }),
  };

  return httpDelete(
    withQueryParamsUrl(`/user-event-groups/${id}/acl`, params),
    { withCreds: true },
  );
};
